import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { Settings as SettingsIcon, Plus, Pencil, Trash2, X, Check, Lock } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPanel({ can }) {
  const editable = can("manage_fees");
  return (
    <div className="space-y-6" data-testid="settings-panel">
      <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden">
        <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#648DE5]/15 text-[#0F1B47] flex items-center justify-center">
            <SettingsIcon size={18} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Fee heads</h2>
            <p className="text-sm text-[#4A5568] mt-0.5 max-w-2xl">
              Define the fee categories your school charges (e.g. Tuition Fee, Transport, Books). Admins pick a subset of these and fill in amounts when generating a fee receipt. The four default heads are seeded for you — you can rename or disable them, but not delete.
            </p>
          </div>
        </div>
        <div className="p-6 md:p-7">
          <FeeHeadsList editable={editable} />
        </div>
      </div>
    </div>
  );
}

function FeeHeadsList({ editable }) {
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/fee-heads");
      setHeads(data || []);
    } catch (e) {
      // 401 handled globally
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const addHead = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return toast.error("Enter a name");
    setAdding(true);
    try {
      await adminApi.post("/admin/fee-heads", { name });
      toast.success(`"${name}" added`);
      setNewName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add fee head");
    } finally { setAdding(false); }
  };

  const startEdit = (h) => {
    setEditingId(h.id);
    setEditingName(h.name);
  };

  const saveEdit = async (h) => {
    const name = editingName.trim();
    if (!name) return toast.error("Name cannot be empty");
    if (name === h.name) { setEditingId(null); return; }
    try {
      await adminApi.patch(`/admin/fee-heads/${h.id}`, { name });
      toast.success("Renamed");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const toggleDisable = async (h) => {
    try {
      await adminApi.patch(`/admin/fee-heads/${h.id}`, { disabled: !h.disabled });
      toast.success(h.disabled ? "Enabled" : "Disabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteHead = async (h) => {
    if (!window.confirm(`Delete "${h.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/fee-heads/${h.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div>
      {editable && (
        <form onSubmit={addHead} className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="fee-head-add-form">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New fee head name (e.g. Transport Fee)"
            className="flex-1 bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
            data-testid="fee-head-new-name"
            maxLength={80}
          />
          <button
            type="submit"
            disabled={adding || !newName.trim()}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
            data-testid="fee-head-add-btn"
          >
            <Plus size={14} /> {adding ? "Adding…" : "Add fee head"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-[#718096] py-6 text-center">Loading fee heads…</div>
      ) : heads.length === 0 ? (
        <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
          <div className="text-[#0F1B47] font-medium">No fee heads yet</div>
        </div>
      ) : (
        <ul className="divide-y divide-[#0F1B47]/8 bg-[#F5F5F0] rounded-2xl overflow-hidden" data-testid="fee-heads-list">
          {heads.map((h) => (
            <li key={h.id} className="px-4 py-3 flex items-center gap-3" data-testid={`fee-head-${h.id}`}>
              <div className="flex-1 min-w-0">
                {editingId === h.id ? (
                  <input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(h); if (e.key === "Escape") setEditingId(null); }}
                    autoFocus
                    className="w-full bg-[#FDFDF9] border border-[#648DE5] rounded-lg px-3 py-1.5 text-sm text-[#0F1B47] focus:outline-none"
                    data-testid={`fee-head-edit-input-${h.id}`}
                  />
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-medium ${h.disabled ? "text-[#718096] line-through" : "text-[#0F1B47]"}`}>{h.name}</span>
                    {h.is_default && (
                      <span className="text-[9px] font-bold tracking-[0.18em] uppercase rounded-full px-2 py-0.5 bg-[#C6D947]/30 text-[#0F1B47]">Default</span>
                    )}
                    {h.disabled && (
                      <span className="text-[9px] font-bold tracking-[0.18em] uppercase rounded-full px-2 py-0.5 bg-[#FCDFDF] text-red-700">Disabled</span>
                    )}
                  </div>
                )}
              </div>
              {editable && (
                <div className="flex items-center gap-1 shrink-0">
                  {editingId === h.id ? (
                    <>
                      <button
                        onClick={() => saveEdit(h)}
                        className="p-2 rounded-full text-emerald-700 hover:bg-emerald-50"
                        title="Save"
                        data-testid={`fee-head-save-${h.id}`}
                      >
                        <Check size={15} />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-2 rounded-full text-[#4A5568] hover:bg-[#0F1B47]/5"
                        title="Cancel"
                      >
                        <X size={15} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(h)}
                        className="p-2 rounded-full text-[#0F1B47] hover:bg-[#0F1B47]/5"
                        title="Rename"
                        data-testid={`fee-head-edit-${h.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => toggleDisable(h)}
                        className={`text-[11px] font-bold tracking-wide uppercase px-3 py-1.5 rounded-full ${h.disabled ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-[#0F1B47]/5 text-[#0F1B47] hover:bg-[#0F1B47]/10"}`}
                        data-testid={`fee-head-toggle-${h.id}`}
                      >
                        {h.disabled ? "Enable" : "Disable"}
                      </button>
                      {h.is_default ? (
                        <span className="p-2 text-[#A0AEC0]" title="Default head — cannot be deleted, only disabled" aria-label="Default head — cannot be deleted">
                          <Lock size={13} />
                        </span>
                      ) : (
                        <button
                          onClick={() => deleteHead(h)}
                          className="p-2 rounded-full text-red-600 hover:bg-red-50"
                          title="Delete"
                          data-testid={`fee-head-delete-${h.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
